import React, { useRef, useEffect, useState } from 'react';
import { FusedRecord } from '../../core/types/fusion';
import { RotateCcw, Video } from 'lucide-react';

interface ThreeDViewProps {
  records: FusedRecord[];
  isCalibrated?: boolean;
}

export const ThreeDView: React.FC<ThreeDViewProps> = ({ records, isCalibrated = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Initial 3D Camera Orbit State: Shallow pitch for flat horizontal ground alignment
  const [pitch, setPitch] = useState<number>(0.18); // Shallow pitch so horizontal movement stays parallel to floor
  const [yaw, setYaw] = useState<number>(0.25);   // Subtle 3D perspective angle
  const [zoom, setZoom] = useState<number>(1.0);   // Zoom factor
  const [colorMode, setColorMode] = useState<'dbfs' | 'frequency' | 'time'>('dbfs');
  const [showCameras, setShowCameras] = useState<boolean>(false); // Default: hidden as requested

  const isDraggingRef = useRef<boolean>(false);
  const lastMouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const resetView = () => {
    setPitch(0.18);
    setYaw(0.25);
    setZoom(1.0);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const cx = width / 2;
    const cy = height / 2;

    // Clear Background
    ctx.fillStyle = '#0b1120';
    ctx.fillRect(0, 0, width, height);

    // 1. Extract 3D Points from Fused Records
    const points3D: Array<{ x: number; y: number; z: number; record: FusedRecord }> = [];

    for (const rec of records) {
      const posA = rec.cameraAPosition;
      const posB = rec.cameraBPosition;

      if (posA && posA.status === 'tracking') {
        // Horizontal motion mapping: (320 - xA)/120 -> moves left-to-right
        const wx = (320 - posA.filteredPosition.x) / 120;
        // Vertical motion mapping: (240 - yA)/120 -> up/down
        const wy = (240 - posA.filteredPosition.y) / 120;

        let wz = 0;
        if (posB && posB.status === 'tracking') {
          // Depth / Disparity relative offset
          const disparity = (posA.filteredPosition.x - posB.filteredPosition.x) / 60;
          wz = disparity;
        }

        points3D.push({ x: wx, y: wy, z: wz, record: rec });
      }
    }

    // 2. Dynamic Trajectory Bounds & Centroid Calculation
    let minX = -1.5, maxX = 1.5;
    let minY = -0.5, maxY = 0.5;
    let minZ = -1.5, maxZ = 1.5;

    if (points3D.length > 0) {
      minX = Math.min(...points3D.map((p) => p.x));
      maxX = Math.max(...points3D.map((p) => p.x));
      minY = Math.min(...points3D.map((p) => p.y));
      maxY = Math.max(...points3D.map((p) => p.y));
      minZ = Math.min(...points3D.map((p) => p.z));
      maxZ = Math.max(...points3D.map((p) => p.z));
    }

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;

    const spanX = Math.max(3.0, maxX - minX + 2.0);
    const spanZ = Math.max(3.0, maxZ - minZ + 2.0);

    // Dynamic Grid Floor bounds encompassing all actual trajectory points
    const gridMinX = Math.floor(minX - 1.2);
    const gridMaxX = Math.ceil(maxX + 1.2);
    const gridMinZ = Math.floor(minZ - 1.2);
    const gridMaxZ = Math.ceil(maxZ + 1.2);
    const gridY = minY - 0.5; // Floor sits flat below lowest trajectory point

    const focalLength = (350 / Math.max(1, Math.max(spanX, spanZ) / 4)) * zoom;
    const camDist = 4.5;

    // 3D Projection Helper centered at trajectory centroid
    const project3D = (x: number, y: number, z: number): { px: number; py: number; depth: number } => {
      const dx = x - centerX;
      const dy = y - centerY;
      const dz = z - centerZ;

      // 1. Rotate around Y axis (yaw)
      const x1 = dx * Math.cos(yaw) - dz * Math.sin(yaw);
      const z1 = dx * Math.sin(yaw) + dz * Math.cos(yaw);

      // 2. Rotate around X axis (pitch)
      const y2 = dy * Math.cos(pitch) - z1 * Math.sin(pitch);
      const z2 = dy * Math.sin(pitch) + z1 * Math.cos(pitch);

      // 3. Camera distance translation
      const zCam = z2 + camDist;

      // 4. Perspective Projection to Screen
      const scale = focalLength / Math.max(0.1, zCam);
      const px = cx + x1 * scale;
      const py = cy - y2 * scale;

      return { px, py, depth: zCam };
    };

    // Draw Flat Horizontal 3D Grid Floor (Encompassing actual motion data)
    ctx.strokeStyle = 'rgba(51, 65, 85, 0.4)';
    ctx.lineWidth = 1;

    const step = 0.5;
    for (let gx = gridMinX; gx <= gridMaxX; gx += step) {
      const p1 = project3D(gx, gridY, gridMinZ);
      const p2 = project3D(gx, gridY, gridMaxZ);
      ctx.beginPath();
      ctx.moveTo(p1.px, p1.py);
      ctx.lineTo(p2.px, p2.py);
      ctx.stroke();
    }

    for (let gz = gridMinZ; gz <= gridMaxZ; gz += step) {
      const p3 = project3D(gridMinX, gridY, gz);
      const p4 = project3D(gridMaxX, gridY, gz);
      ctx.beginPath();
      ctx.moveTo(p3.px, p3.py);
      ctx.lineTo(p4.px, p4.py);
      ctx.stroke();
    }

    // Draw Camera Position Nodes (Only when user toggles showCameras to true)
    if (showCameras) {
      const camA3D = project3D(centerX, maxY + 0.8, gridMinZ);
      ctx.fillStyle = '#84cc16';
      ctx.beginPath();
      ctx.arc(camA3D.px, camA3D.py, 5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.font = '11px sans-serif';
      ctx.fillText('Camera A (正前方)', camA3D.px - 32, camA3D.py - 10);

      const camB3D = project3D(gridMaxX, maxY + 0.8, centerZ);
      ctx.fillStyle = '#0ea5e9';
      ctx.beginPath();
      ctx.arc(camB3D.px, camB3D.py, 5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillText('Camera B (右侧)', camB3D.px - 24, camB3D.py - 10);
    }

    // If no records, draw prompt
    if (records.length === 0) {
      ctx.fillStyle = '#64748b';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('拖拽鼠标可 360° 旋转三维视角 | 滚动鼠标滚轮可缩放空间', cx, cy + 180);
      ctx.fillText('等待双摄采集数据中... 开始采集后将实时重构 3D 轨迹与声场', cx, cy);
      return;
    }

    // Render 3D Trajectory Lines
    if (points3D.length > 1) {
      ctx.lineWidth = 1.8;
      for (let i = 1; i < points3D.length; i++) {
        const ptPrev = project3D(points3D[i - 1].x, points3D[i - 1].y, points3D[i - 1].z);
        const ptCurr = project3D(points3D[i].x, points3D[i].y, points3D[i].z);

        ctx.strokeStyle = 'rgba(132, 204, 22, 0.7)';
        ctx.beginPath();
        ctx.moveTo(ptPrev.px, ptPrev.py);
        ctx.lineTo(ptCurr.px, ptCurr.py);
        ctx.stroke();
      }
    }

    // Render 3D Acoustic Field Spheres
    for (let i = 0; i < points3D.length; i++) {
      const item = points3D[i];
      const proj = project3D(item.x, item.y, item.z);
      const rec = item.record;

      let color = '#84cc16';
      if (colorMode === 'dbfs') {
        const norm = Math.max(0, Math.min(1, (rec.audioFeatures.dbfs + 90) / 90));
        const hue = (1 - norm) * 240;
        color = `hsl(${hue}, 90%, 55%)`;
      } else if (colorMode === 'frequency') {
        const norm = Math.max(0, Math.min(1, rec.audioFeatures.dominantFrequencyHz / 2000));
        const hue = norm * 300;
        color = `hsl(${hue}, 90%, 55%)`;
      } else {
        const norm = i / points3D.length;
        const hue = norm * 260;
        color = `hsl(${hue}, 80%, 55%)`;
      }

      const radius = Math.max(1.5, Math.min(3.5, 45 / proj.depth));

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(proj.px, proj.py, radius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(proj.px, proj.py, 0.8, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [records, pitch, yaw, zoom, colorMode, showCameras]);

  // Mouse Interaction handlers for 3D Camera Orbit
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    isDraggingRef.current = true;
    lastMouseRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - lastMouseRef.current.x;
    const dy = e.clientY - lastMouseRef.current.y;
    lastMouseRef.current = { x: e.clientX, y: e.clientY };

    setYaw((prev) => prev + dx * 0.008);
    setPitch((prev) => Math.max(-Math.PI / 2.2, Math.min(Math.PI / 2.2, prev + dy * 0.008)));
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((prev) => Math.max(0.4, Math.min(3.0, prev * delta)));
  };

  return (
    <div>
      {/* Top Status & Controls Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className={`status-badge ${isCalibrated ? 'status-running' : 'status-degraded'}`}>
            {isCalibrated ? '标定 3D / Calibrated 3D' : '虚拟 3D / Virtual 3D (Uncalibrated Disparity)'}
          </span>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            💡 水平地面自适应置于轨迹下方；按住鼠标左键可 360° 旋转观察视角
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className={`btn btn-outline ${showCameras ? 'btn-primary' : ''}`}
            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
            onClick={() => setShowCameras(!showCameras)}
          >
            <Video size={12} style={{ marginRight: 4 }} />
            {showCameras ? '隐藏 摄像头位置' : '显示 摄像头位置'}
          </button>
          <button
            className={`btn btn-outline ${colorMode === 'dbfs' ? 'btn-primary' : ''}`}
            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
            onClick={() => setColorMode('dbfs')}
          >
            按声强 (dBFS) 3D 着色
          </button>
          <button
            className={`btn btn-outline ${colorMode === 'frequency' ? 'btn-primary' : ''}`}
            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
            onClick={() => setColorMode('frequency')}
          >
            按主频 (Hz) 3D 着色
          </button>
          <button
            className={`btn btn-outline ${colorMode === 'time' ? 'btn-primary' : ''}`}
            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
            onClick={() => setColorMode('time')}
          >
            按时间演进着色
          </button>
          <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={resetView}>
            <RotateCcw size={12} /> 重置视角
          </button>
        </div>
      </div>

      {/* Interactive 3D Canvas */}
      <div className="camera-card" style={{ width: '100%', height: '540px', position: 'relative' }}>
        <canvas
          ref={canvasRef}
          width={960}
          height={540}
          style={{ width: '100%', height: '100%', display: 'block', cursor: 'grab' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        />
      </div>
    </div>
  );
};
